import {
  buildPath,
  buildTemplatePath,
  CrudMode,
  Page,
  PageRoute,
  Stack,
  useParams,
  useUnsavedChanges,
  useValidationErrors,
} from "@compass/core";
import { Permission, PermissionAction, Provider, ProviderEditResource, ProviderViewResource } from "generated/openApi";
import "moment-timezone";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { isAuthorized } from "services";
import ApiService from "services/apiService";
import { Employments } from "./Employments";
import { ProviderBasicInfo } from "./ProviderBasicInfo";
import { ProviderPageHeader } from "./ProviderPageHeader";

const routeName = "provider";

class Params {
  id: number = 0;
  crudMode: CrudMode = "view";
}

export const providerPageRoute: PageRoute<Params> = {
  handle: "Provider",
  templatePath: buildTemplatePath(routeName, Params),
  getPath: (params) => buildPath(routeName, params),
};

export const ProviderPage = () => {
  const ROOT_PERMISSION: Permission = Permission.Provider;
  const { params } = useParams(routeName, Params);
  const navigate = useNavigate();
  const [provider, setProvider] = useState<Provider>({} as Provider);
  const { unsavedChanges, resetUnsavedChanges } = useUnsavedChanges({ trackedItems: [provider] });
  const [viewResource, setViewResource] = useState<ProviderViewResource>({});
  const [editResource, setEditResource] = useState<ProviderEditResource>({} as ProviderEditResource);
  const { validationErrors, setValidationErrors } = useValidationErrors();

  useEffect(() => {
    fetch(params.id);
    fetchViewResource(params.id);
  }, []);

  useEffect(() => {
    if (params.crudMode === "edit") fetchEditResource();
  }, [params.crudMode]);

  const reroute = (id: number, crudMode: CrudMode) =>
    navigate(`../${providerPageRoute.getPath({ id: id, crudMode: crudMode })}`);

  const fetch = (id: number) => {
    ApiService.providers.get(id).then((response) => {
      setProvider(response);
      resetUnsavedChanges([response]);
    });
  };

  const fetchViewResource = (id: number) => {
    ApiService.providers.getViewResource(id).then((response) => {
      setViewResource(response);
    });
  };

  const fetchEditResource = () => {
    ApiService.providers.getEditResource().then((response) => {
      setEditResource(response);
    });
  };

  return (
    <Page
      isForm
      header={
        <ProviderPageHeader
          rootPermission={ROOT_PERMISSION}
          crudMode={params.crudMode}
          reroute={reroute}
          fetch={fetch}
          provider={provider}
          viewResource={viewResource}
          setValidationErrors={setValidationErrors}
          unsavedChanges={unsavedChanges}
        />
      }
    >
      <Stack spacing="md">
        <ProviderBasicInfo
          provider={provider}
          setProvider={setProvider}
          viewResource={viewResource}
          editResource={editResource}
          crudMode={params.crudMode}
          validationErrors={validationErrors}
        />

        {isAuthorized(Permission.ProviderEmployment, PermissionAction.View) && <Employments providerId={params.id} />}
      </Stack>
    </Page>
  );
};
